import { ButtonColors, Modal, Typography } from '@repo/ui';

import React, { useState } from 'react';

import { DesignButton } from 'components';
import c from 'classnames';

import styles from './CarouselDialog.module.css';
export interface SlideItem {
  imgSrc: string;
  heading?: string;
  text?: string;
  buttonLabel?: string;
  buttonAction?: () => void;
  isNextButton?: boolean;
}

interface CarouselComponentProps {
  slides: SlideItem[];
  imageStyle?: React.CSSProperties;
  containerStyle?: React.CSSProperties;
  color?: string;
}

const CarouselComponent: React.FC<CarouselComponentProps> = ({
  slides,
  imageStyle,
  containerStyle,
  color,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToSlide = (index: number) => {
    if (index >= 0 && index < slides.length) {
      setCurrentIndex(index);
    }
  };

  if (!slides || slides.length === 0) {
    return <div className={styles.emptyCarousel}>No slides to display.</div>;
  }

  const currentSlide = slides[currentIndex];

  const handleButtonClick = () => {
    if (currentSlide.isNextButton && currentIndex < slides.length - 1) {
      goToSlide(currentIndex + 1);
    } else if (currentSlide.buttonAction) {
      currentSlide.buttonAction();
    }
  };

  const dotColor = styles[`dotColor${color}`];

  return (
    <div className={styles.carouselContainer} style={containerStyle}>
      <div className={styles.slideWrapper}>
        {currentSlide.heading && (
          <Typography variant='heading-xl' responsive>
            {currentSlide.heading}
          </Typography>
        )}
        <div className={c(styles.imageWrapper, 'flex align-items-center')}>
          <img
            src={currentSlide.imgSrc}
            alt={currentSlide.heading ?? `Slide ${currentIndex + 1} image`}
            className={styles.carouselImage}
            style={imageStyle}
          />
        </div>
        {currentSlide.text &&
          currentSlide.text.split('\n').map((line, index) => (
            <Typography key={index} variant='heading-m' responsive>
              {line}
            </Typography>
          ))}
        {currentSlide.buttonLabel &&
          (currentSlide.buttonAction || currentSlide.isNextButton) && (
            <DesignButton
              color={ButtonColors.PURPLE}
              label={currentSlide.buttonLabel}
              onClick={handleButtonClick}
            />
          )}
      </div>
      <div className={styles.dotsContainer}>
        {slides.map((_, index) => (
          <button
            key={index}
            type='button'
            aria-label={`Go to slide ${index + 1}`}
            className={c(
              styles.dot,
              currentIndex === index ? styles.activeDot : '',
              dotColor,
            )}
            onClick={() => goToSlide(index)}
          />
        ))}
      </div>
    </div>
  );
};

interface CarouselDialogProps {
  isVisible: boolean;
  slides: SlideItem[];
  imageStyle?: React.CSSProperties;
  popupClassName?: string;
  onClose?: () => void;
  color?: string;
}

const CarouselDialog: React.FC<CarouselDialogProps> = ({
  isVisible,
  slides,
  imageStyle,
  popupClassName,
  onClose,
  color,
}) => {
  if (!isVisible) {
    return null;
  }

  // NOTE: If you need a new color for this popup, don't forget to add the styles in CarouselDialog.module.css
  const popupColor = styles[`popupColor${color}`];

  return (
    <Modal
      isOpen={true}
      className={c(popupClassName, popupColor)}
      onClose={onClose}
      showFloatingX
    >
      <div className={styles.container}>
        <CarouselComponent
          slides={slides}
          imageStyle={imageStyle}
          color={color}
        />
      </div>
    </Modal>
  );
};

export default CarouselDialog;
